import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const [flagImages, setFlagImages] = useState({
    en: '/flags/england.png',
    it: '/flags/italy.png'
  });
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLanguageChange = () => {
    const newLanguage = currentLanguage === 'en' ? 'it' : 'en';
    i18n.changeLanguage(newLanguage);
    // Reset image error state when language changes
    setImageError(false);
  };

  // Show flag of language user can switch TO (not current language)
  const getFlagImage = () => {
    const targetLanguage = currentLanguage === 'en' ? 'it' : 'en';
    const flagImage = flagImages[targetLanguage as keyof typeof flagImages];
    // Add cache-busting parameter to force fresh load
    const cacheBuster = Date.now();
    const imageUrl = `${flagImage}?t=${cacheBuster}`;
    console.log(`Current language: ${currentLanguage}, Target language: ${targetLanguage}, Flag image: ${imageUrl}`);
    return imageUrl;
  };



  const getTooltipText = () => {
    return currentLanguage === 'en' ? 'Switch to Italian' : 'Switch to English';
  };

  // Handle pasted images
  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            // Determine which flag this is based on current language
            const targetLanguage = currentLanguage === 'en' ? 'it' : 'en';
            const fileName = targetLanguage === 'it' ? 'italy.png' : 'england.png';
            
            // Create a blob URL for the pasted image
            const blobUrl = URL.createObjectURL(file);
            
            // Update the flag image
            setFlagImages(prev => ({
              ...prev,
              [targetLanguage]: blobUrl
            }));

            // Save to localStorage for persistence
            const savedFlags = JSON.parse(localStorage.getItem('flagImages') || '{}');
            savedFlags[targetLanguage] = blobUrl;
            localStorage.setItem('flagImages', JSON.stringify(savedFlags));

            console.log(`Flag image updated for ${targetLanguage}: ${fileName}`);
            break;
          }
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, []); // Removed currentLanguage dependency to prevent re-registration

  // Load saved flag images on component mount
  useEffect(() => {
    const savedFlags = localStorage.getItem('flagImages');
    if (savedFlags) {
      try {
        const parsed = JSON.parse(savedFlags);
        // Only use saved flags if they are valid file paths (not blob URLs)
        const validFlags: { [key: string]: string } = {};
        Object.keys(parsed).forEach(key => {
          if (parsed[key] && !parsed[key].startsWith('blob:')) {
            validFlags[key] = parsed[key];
          }
        });
        setFlagImages(prev => ({ ...prev, ...validFlags }));
        
        // Clear localStorage to remove invalid blob URLs
        localStorage.removeItem('flagImages');
      } catch (error) {
        console.error('Error loading saved flag images:', error);
        // Clear localStorage on error
        localStorage.removeItem('flagImages');
      }
    }
  }, []);

  // Reset image error state when language changes
  useEffect(() => {
    setImageError(false);
  }, [currentLanguage]);

  // Handle file input change (for manual file selection)
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    
    if (file && file.type.startsWith('image/')) {
      const targetLanguage = currentLanguage === 'en' ? 'it' : 'en';
      const blobUrl = URL.createObjectURL(file);
      
      setFlagImages(prev => ({
        ...prev,
        [targetLanguage]: blobUrl
      }));

      // Save to localStorage
      const savedFlags = JSON.parse(localStorage.getItem('flagImages') || '{}');
      savedFlags[targetLanguage] = blobUrl;
      localStorage.setItem('flagImages', JSON.stringify(savedFlags));
    }
    
    // Reset the input value so the same file can be selected again
    event.target.value = '';
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <Tooltip title={getTooltipText()} placement="left">
        <IconButton
          onClick={handleLanguageChange}
          onContextMenu={(e) => {
            e.preventDefault();
            fileInputRef.current?.click();
          }}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1300,
            width: 50,
            height: 50,
            padding: 0,
            overflow: 'hidden',
            borderRadius: '50%',
            backdropFilter: 'blur(10px)',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255, 255, 255, 0.5)',
            },
          }}
        >

          {imageError ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0f0f0',
                borderRadius: '50%',
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: '#666',
              }}
            >
              {currentLanguage === 'en' ? 'IT' : 'EN'}
            </Box>
          ) : (
            <Box
              component="img"
              key={`flag-${currentLanguage}`} // Force re-render when language changes
              src={getFlagImage()}
              alt={`Switch to ${currentLanguage === 'en' ? 'Italian' : 'English'}`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
              onError={() => {
                console.error(`Failed to load flag image: ${getFlagImage()}`);
                setImageError(true);
              }}
              onLoad={() => {
                console.log(`Successfully loaded flag image: ${getFlagImage()}`);
                setImageError(false);
              }}
            />
          )}
        </IconButton>
      </Tooltip>
    </>
  );
};

export default LanguageSwitcher;
